<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <brand-logo class="logo auth-logo" />
        <h3 class="text-title mb-2">Confirm reset password</h3>
        <p class="reset-password-description">
          Requires a new registration code sent to you by email.
        </p>
      </div>

      <v-form ref="form" v-model="valid">
        <div class="auth-card-container form-fields" style="margin-bottom: 8px">
          <v-text-field
            label="Email"
            type="email"
            v-model="username"
            :rules="[rules.required, rules.email]"
            validate-on="blur"
            variant="outlined"
            density="default"
            ref="email"
          ></v-text-field>

          <v-text-field
            label="Registration code"
            type="text"
            v-model="code"
            :rules="[rules.required]"
            validate-on="blur"
            variant="outlined"
            density="default"
            ref="code"
          ></v-text-field>

          <v-text-field
            label="New password"
            v-model="pass"
            :append-inner-icon="e1 ? 'mdi-eye-off' : 'mdi-eye'"
            @click:append-inner="() => (e1 = !e1)"
            :type="e1 ? 'password' : 'text'"
            min="8"
            :rules="[rules.required, rules.passwordPolicy]"
            :hint="PASSWORD_HINT"
            counter
            validate-on="blur"
            variant="outlined"
            density="default"
            v-on:keydown.enter="submit"
          ></v-text-field>

          <v-row class="my-0">
            <v-col class="pa-0">
              <v-alert
                v-if="codeExpired"
                type="warning"
                variant="tonal"
                density="compact"
                class="mb-2"
              >
                This code has expired.
                <router-link
                  :to="{ path: '/reset-password', query: { username: username || undefined } }"
                  class="text-decoration-none"
                >
                  Request a new code
                </router-link>
              </v-alert>
              <p v-else-if="error" class="bg-error">{{ error }}</p>
              <p v-if="message" class="message">{{ message }}</p>
            </v-col>
          </v-row>

          <div class="d-flex justify-center auth-card-container">
            <v-btn
              min-width="180px"
              rounded="lg"
              size="large"
              color="primary"
              class="d-flex"
              variant="flat"
              @click="submit"
            >
              Confirm Reset Password
            </v-btn>
          </div>

          <v-divider></v-divider>

          <div class="auth-card-footer-part">
            <div class="auth-card-footer">
              <p class="text">
                Go back to login?
                <router-link :to="{ path: '/login' }" tabindex="5">
                  <span class="light--text">Login</span>
                </router-link>
              </p>
            </div>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
// eslint-disable-next-line no-unused-vars
import { passwordValidationRule, PASSWORD_HINT } from '@/helpers/passwordValidation'
import { authProxyApi } from '@/api/authProxyApi'
import BrandLogo from '@/components/BrandLogo.vue'

export default {
  components: {
    BrandLogo
  },
  data () {
    return {
      username: '',
      code: '',
      pass: '',
      error: null,
      valid: true,
      message: null,
      codeExpired: false,
      rules: {
        required: (value) => !!value || 'Required.',
        email: (value) => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        },
        passwordPolicy: passwordValidationRule
      },
      e1: true
    }
  },
  methods: {
    async submit (event) {
      event.preventDefault()
      this.error = null
      this.message = null
      this.codeExpired = false
      const { valid } = await this.$refs.form.validate()
      if (valid) {
        try {
          const email = (this.username || '').toLowerCase()
          const code = (this.code || '').trim()
          const newPassword = this.pass

          const res = await authProxyApi.confirmForgotPassword({ email, code, newPassword })

          if (res?.code === 'CODE_EXPIRED') {
            // No resend on this page — nudge back to /reset-password.
            this.codeExpired = true
            return
          }

          if (!res?.ok) {
            this.error = res?.message || 'Unable to verify code. Please check and try again.'
            return
          }

          // Password reset automatically invalidates all existing sessions for security
          this.$router.replace({ path: '/login', query: { resetPasswordSuccess: true, username: email } })
        } catch (err) {
          console.error('Confirm reset password failed:', err)
          this.error = 'Unable to verify code. Please check and try again.'
        }
      }
    }
  },
  created () {
    if (this.$route.query.username !== '') {
      this.username = this.$route.query.username
    }
  },
  mounted () {
    if (this.$route.query.username) {
      this.$refs.code.focus()
    } else {
      this.$refs.email.focus()
    }
  }
}
</script>

<style scoped>
.text {
  font-size: 13px;
  margin-bottom: 0;
  line-height: 14.3px;
}

.logo {
  width: 110px;
  height: 48px;
  margin-bottom: 24px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}

[data-theme="dark"] a,
[data-theme="dark"] a .light--text {
  color: var(--blue-1);
}
</style>
